import { Component, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { CurrencyFormatPipe, rxResourceFixed } from '@zayan-erp/shared';
import { TagModule } from '@openng/optimus-ui/tag';
import { GalleriaModule } from '@openng/optimus-ui/galleria';
import { ButtonModule } from '@openng/optimus-ui/button';
import { ButtonGroup } from '@openng/optimus-ui/buttongroup';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { map, of } from 'rxjs';
import {
  DsmProductAttributeGroup,
  DsmProductAttributesResponse,
  DsmProductOption,
  DsmSelectedProductValues
} from '../../../models/dsm-deal-options.models';
import { DsmSelectedProductConfigFormComponent } from '../dsm-selected-product-config-form/dsm-selected-product-config-form.component';
import { DsmModelElevationProductsService } from '../../../services/dsm-model-elevation-products.service';
import { DsmPackageService } from '../../../services/dsm-package.service';
import { LocationItem } from '@zayan-erp/home-building';

interface DsmLinkedPanelView {
  product: DsmProductOption;
  values: DsmSelectedProductValues;
  attributes: DsmProductAttributeGroup[];
}

interface DsmReadonlyAttributeRow {
  label: string;
  value: string;
}

@Component({
  selector: 'dsm-selected-product-panel',
  standalone: true,
  imports: [
    NgClass,
    NgTemplateOutlet,
    CurrencyFormatPipe,
    TagModule,
    GalleriaModule,
    ButtonModule,
    ButtonGroup,
    DialogModule,
    DsmSelectedProductConfigFormComponent,
    DsmSelectedProductPanelComponent
  ],
  templateUrl: './dsm-selected-product-panel.component.html'
})
export class DsmSelectedProductPanelComponent {
  readonly #modelElevationProductsService = inject(DsmModelElevationProductsService);
  readonly #packageService = inject(DsmPackageService);

  readonly product = input.required<DsmProductOption>();
  readonly values = input.required<DsmSelectedProductValues>();
  readonly addendumId = input<string | null>(null);
  // Location source before an addendum exists (appointment screens); ignored when addendumId is set.
  readonly lotId = input<string | null>(null);
  readonly attributeGroups = input<DsmProductAttributeGroup[] | null>(null);
  readonly preloadedLocations = input<LocationItem[] | null>();
  readonly showDeleteIcon = input<boolean>(false);
  readonly showEditIcon = input<boolean>(true);
  readonly inPopup = input<boolean>(false);
  readonly quantityEditable = input<boolean>(false);
  readonly missingFieldsCount = input<number>(0);
  readonly nested = input<boolean>(false);
  readonly editable = input<boolean>(false);
  readonly discountPercentage = input<number>(0);
  readonly removeRequested = output<void>();
  readonly editRequested = output<void>();
  readonly edited = output<DsmSelectedProductValues>();
  readonly quantityChanged = output<number>();
  readonly linkedProductUpdated = output<{ modelElevationProductsId: string; values: DsmSelectedProductValues }>();

  readonly isExpanded = signal(false);
  readonly isEditingInline = signal(false);
  readonly inlineConfigFormRef = viewChild<DsmSelectedProductConfigFormComponent>('inlineConfigForm');
  readonly editingLinkedIndex = signal<number | null>(null);
  readonly linkedConfigFormRef = viewChild<DsmSelectedProductConfigFormComponent>('linkedConfigForm');

  readonly imageGalleryVisible = signal(false);
  readonly activeImageIndex = signal(0);

  readonly imageUrls = computed(() =>
    (this.product().images ?? []).map(image => this.#extractImageUrl(image)).filter((url): url is string => !!url)
  );

  readonly galleryImages = computed(() =>
    this.imageUrls().map((url, index) => ({
      itemImageSrc: url,
      thumbnailImageSrc: url,
      alt: `${this.product().code || 'Product'} image ${index + 1}`,
      title: this.product().code || 'Product image'
    }))
  );

  readonly primaryImageUrl = computed(() => this.imageUrls()[0] || 'assets/images/img-placeholder.png');

  readonly activeKitTab = signal<'setup' | 'linked'>('setup');

  readonly productAttributesResource = rxResourceFixed({
    params: () => ({
      productId: this.product().productId,
      addendumId: this.addendumId(),
      isKit: this.product().isKit,
      hasProvidedGroups: this.attributeGroups() != null
    }),
    defaultValue: null as DsmProductAttributesResponse | null,
    stream: ({ params }) => {
      // Groups supplied by the parent (e.g. a kit's linked attributes) — don't fetch again.
      if (params.hasProvidedGroups || !params.productId || !params.addendumId) {
        return of<DsmProductAttributesResponse | null>(null);
      }
      // A kit returns the grouped object (own + linked attributes); a regular product an array.
      return params.isKit
        ? this.#modelElevationProductsService.getProductAttributesGrouped(params.productId)
        : this.#modelElevationProductsService.getProductAttributes(params.productId).pipe(
            map(
              attributes =>
                ({
                  productId: params.productId,
                  isKit: false,
                  attributes,
                  linkedProducts: []
                }) as DsmProductAttributesResponse
            )
          );
    }
  });

  readonly #attributeGroups = computed<DsmProductAttributeGroup[]>(
    () => this.attributeGroups() ?? this.productAttributesResource.value()?.attributes ?? []
  );

  readonly #linkedAttributesByProductId = computed(() => {
    const map = new Map<string, DsmProductAttributeGroup[]>();
    (this.productAttributesResource.value()?.linkedProducts ?? []).forEach(linked =>
      map.set(linked.productId, linked.attributes ?? [])
    );
    return map;
  });

  readonly productLocationsResource = rxResourceFixed({
    params: () => {
      const preloaded = this.preloadedLocations();
      if (preloaded === null) return undefined;
      return { productId: this.product().productId, addendumId: this.addendumId(), lotId: this.lotId(), preloaded };
    },
    defaultValue: [] as LocationItem[],
    stream: ({ params }) => {
      if (params.preloaded !== undefined) return of<LocationItem[]>(params.preloaded);
      if (!params.productId) return of<LocationItem[]>([]);
      return this.#modelElevationProductsService.getScopedProductLocations(params, params.productId);
    }
  });

  readonly locationLabel = computed(() => {
    const locationId = this.values().locationId;
    if (!locationId) return '—';
    const location = (this.productLocationsResource.value() ?? []).find(item => item.id === locationId);
    const code = location?.locationCode;
    const name = location?.locationName;
    return code && name ? `${code} - ${name}` : code || name || '—';
  });

  readonly attributeRows = computed<DsmReadonlyAttributeRow[]>(() => {
    const attributes = this.#attributeGroups();
    const selected = this.values().attributeValues;
    return attributes.map(attribute => {
      const selectedValueId = selected[attribute.subCategoryAttributeId];
      const selectedValue = attribute.productAttributeValues.find(value => value.id === selectedValueId);
      return {
        label: attribute.subCategoryAttributeName,
        value: selectedValue?.value ?? '—'
      };
    });
  });

  // Selected attribute values joined for the one-line popup summary (e.g. "Matte Black · Stainless Steel").
  readonly attributeSummary = computed(() =>
    this.attributeRows()
      .map(row => row.value)
      .filter(value => value && value !== '—')
      .join(' · ')
  );

  readonly missingFieldsLabel = computed(
    () => `${this.missingFieldsCount()} MISSING FIELD${this.missingFieldsCount() === 1 ? '' : 'S'}`
  );

  readonly totalPrice = computed(() => (this.values().quantity ?? 0) * (this.values().unitPrice ?? 0));

  readonly hasDiscount = computed(() => (this.discountPercentage() ?? 0) > 0);

  readonly discountedUnitPrice = computed(() =>
    this.#packageService.amountAfterDiscount(this.values().unitPrice ?? 0, this.discountPercentage())
  );

  readonly discountedTotalPrice = computed(() =>
    this.#packageService.amountAfterDiscount(this.totalPrice(), this.discountPercentage())
  );

  readonly notesDisplay = computed(() => this.values().notes?.trim() || '—');

  readonly isKit = computed(() => this.product().isKit);

  // A kit's configured linked products rendered as nested panels (read-only) in the
  // expanded "Linked Options" tab.
  readonly linkedPanelViews = computed<DsmLinkedPanelView[]>(() => {
    const linkedValues = this.values().linkedProducts ?? [];
    const linkedProducts = this.product().linkedProducts ?? [];
    const attributeMap = this.#linkedAttributesByProductId();

    return linkedValues
      .map(linked => {
        const match = linkedProducts.find(item => item.modelElevationProductsId === linked.modelElevationProductsId);
        if (!match) return null;
        return {
          product: match,
          values: {
            locationId: linked.locationId,
            quantity: linked.quantity,
            unitPrice: linked.unitPrice,
            notes: linked.notes,
            attributeValues: linked.attributeValues
          },
          attributes: match.productId ? (attributeMap.get(match.productId) ?? []) : []
        } as DsmLinkedPanelView;
      })
      .filter((view): view is DsmLinkedPanelView => view != null);
  });

  readonly editingLinkedView = computed<DsmLinkedPanelView | null>(() => {
    const index = this.editingLinkedIndex();
    return index != null ? (this.linkedPanelViews()[index] ?? null) : null;
  });

  remove(): void {
    this.removeRequested.emit();
  }

  changeQuantity(delta: number): void {
    const next = Math.max(1, (this.values().quantity ?? 0) + delta);
    if (next !== this.values().quantity) {
      this.quantityChanged.emit(next);
    }
  }

  toggleExpanded(event: Event): void {
    event.stopPropagation();
    this.isExpanded.update(expanded => !expanded);
    if (!this.isExpanded()) {
      this.isEditingInline.set(false);
    }
  }

  onEdit(event: Event): void {
    event.stopPropagation();
    if (this.editable()) {
      this.isExpanded.set(true);
      this.isEditingInline.set(true);
      return;
    }
    this.editRequested.emit();
  }

  cancelInlineEdit(): void {
    this.isEditingInline.set(false);
  }

  confirmInlineEdit(): void {
    this.inlineConfigFormRef()?.attemptCommit();
  }

  onInlineCommit(values: DsmSelectedProductValues): void {
    this.edited.emit(values);
    this.isEditingInline.set(false);
  }

  openLinkedEdit(index: number): void {
    this.editingLinkedIndex.set(index);
  }

  closeLinkedEdit(): void {
    this.editingLinkedIndex.set(null);
  }

  onLinkedDialogVisibility(visible: boolean): void {
    if (!visible) this.closeLinkedEdit();
  }

  confirmLinkedEdit(): void {
    this.linkedConfigFormRef()?.attemptCommit();
  }

  onLinkedEditCommit(values: DsmSelectedProductValues): void {
    const view = this.editingLinkedView();
    if (!view) return;
    this.linkedProductUpdated.emit({ modelElevationProductsId: view.product.modelElevationProductsId, values });
    this.closeLinkedEdit();
  }

  openImageGallery(event: Event): void {
    event.stopPropagation();
    if (!this.galleryImages().length) return;
    this.activeImageIndex.set(0);
    this.imageGalleryVisible.set(true);
  }

  #extractImageUrl(image: unknown): string | null {
    if (!image || typeof image !== 'object') return null;
    const candidate = image as {
      document?: { directUrl?: string | null };
      directUrl?: string | null;
      url?: string | null;
    };
    return candidate.document?.directUrl || candidate.directUrl || candidate.url || null;
  }
}
